import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { createNote, updateNote, getNoteById, emailNote } from "./NoteService";
import { apiFetch } from "../api";
import { BASE_URL as base } from "../../commonUrls";
import NoteEditor from "./NoteEditor";
import { BUILT_IN_TEMPLATES, saveAsTemplate, getUserTemplates } from "./NoteTemplates";
import NoteGraphPanel from "./NoteGraphPanel";
import NoteHistoryPanel from "./NoteHistoryPanel";
import { useToast } from "../ToastContext";
import "./NoteForm.css";

export default function NoteForm() {
  const nav   = useNavigate();
  const toast = useToast();
  const { state } = useLocation();
  const { id: routeId } = useParams();
  const readOnly = Boolean(state?.readOnly);

  const [editNote, setEditNote] = useState(state?.note || null);

  const [title,           setTitle]           = useState("");
  const [content,         setContent]         = useState("");
  const [noteType,        setNoteType]        = useState("INFO");
  const [priority,        setPriority]        = useState(4);
  const [status,          setStatus]          = useState("ACTIVE");
  const [secured,         setSecured]         = useState(false);
  const [noteSummary,     setNoteSummary]     = useState("");
  const [tags,            setTags]            = useState([]);
  const [tagInput,        setTagInput]        = useState("");
  const [pinned,          setPinned]          = useState(false);
  const [favorite,        setFavorite]        = useState(false);
  const [deleteProtected, setDeleteProtected] = useState(false);
  const [reminderAt,      setReminderAt]      = useState("");
  const [attachments,     setAttachments]     = useState([]);
  const [selectedFiles,   setSelectedFiles]   = useState([]);
  const [uploading,       setUploading]       = useState(false);
  const fileInputRef = useRef(null);
  const [existingFiles,     setExistingFiles]     = useState([]);
  const [fileSearch,        setFileSearch]        = useState("");
  const [selectedExistingIds, setSelectedExistingIds] = useState(new Set());
  const [showTemplatePicker,  setShowTemplatePicker]  = useState(false);
  const [userTemplates,       setUserTemplates]       = useState([]);

  // UI state
  const [showSettings, setShowSettings] = useState(false);
  const [showAttachPanel, setShowAttachPanel] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(""); // "" | "saving" | "saved"

  const autoSaveTimer = useRef(null);
  const isDirty = useRef(false);

  const FILES_URL = `${base}/files`;

  const fileTypeIcon = (fileType) => {
    if (!fileType) return "📎";
    const t = fileType.toLowerCase();
    if (t.includes("pdf")) return "📄";
    if (t.includes("image") || t.includes("png") || t.includes("jpg") || t.includes("jpeg") || t.includes("gif") || t.includes("webp")) return "🖼";
    if (t.includes("word") || t.includes("doc")) return "📝";
    if (t.includes("sheet") || t.includes("excel") || t.includes("csv")) return "📊";
    if (t.includes("video")) return "🎬";
    if (t.includes("audio")) return "🎵";
    if (t.includes("zip") || t.includes("tar") || t.includes("gz")) return "🗜";
    return "📎";
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toInputDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const tzOffset = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffset);
    return local.toISOString().slice(0, 16);
  };

  const applyNoteData = (note) => {
    if (!note) return;
    setTitle(note.title || "");
    setContent(note.content || "");
    setNoteType(note.noteType || "INFO");
    setPriority(note.priority ?? 4);
    setStatus(note.status || "ACTIVE");
    setSecured(Boolean(note.secured));
    setNoteSummary(note.noteSummary || "");
    setTags(Array.isArray(note.tags) ? note.tags : []);
    setPinned(Boolean(note.pinned));
    setFavorite(Boolean(note.favorite));
    setDeleteProtected(Boolean(note.deleteProtected));
    setReminderAt(toInputDateTime(note.reminderAt));
    setAttachments(Array.isArray(note.attachments) ? note.attachments : []);
    const ids = new Set((note.attachments || []).map(f => f.id).filter(Boolean));
    setSelectedExistingIds(ids);
  };

  useEffect(() => {
    if (!editNote?.id) return;
    applyNoteData(editNote);
    let active = true;
    getNoteById(editNote.id).then(note => {
      if (!active || !note) return;
      applyNoteData(note);
    }).catch(() => {});
    return () => { active = false; };
  }, [editNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (routeId && !editNote) {
      getNoteById(routeId).then(note => { if (note) { setEditNote(note); applyNoteData(note); } });
    }
  }, [routeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    apiFetch(FILES_URL).then(res => res.ok ? res.json() : []).then(data => {
      if (!active) return;
      setExistingFiles(Array.isArray(data) ? data : []);
    }).catch(() => { if (!active) return; setExistingFiles([]); });
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build current payload (does not touch attachments/uploads)
  const buildPayload = useCallback(() => {
    const linkedIds = Array.from(selectedExistingIds);
    return {
      title, content, noteType, priority, status, secured, noteSummary, tags,
      pinned, favorite, deleteProtected, reminderAt: reminderAt || null,
      attachmentIds: [...new Set([
        ...attachments.map(f => f.id).filter(Boolean),
        ...linkedIds,
      ])],
    };
  }, [title, content, noteType, priority, status, secured, noteSummary, tags,
      pinned, favorite, deleteProtected, reminderAt, attachments, selectedExistingIds]);

  // Auto-save for existing notes — debounced 2.5 seconds after last content/title change
  useEffect(() => {
    if (!editNote?.id || readOnly) return;
    isDirty.current = true;
    clearTimeout(autoSaveTimer.current);
    setAutoSaveStatus("saving");
    autoSaveTimer.current = setTimeout(async () => {
      if (!isDirty.current) return;
      try {
        await updateNote(editNote.id, buildPayload());
        isDirty.current = false;
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(""), 1800);
      } catch {
        setAutoSaveStatus("");
      }
    }, 2500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [content, title, tags, noteType, priority, status, secured, pinned, favorite, deleteProtected, reminderAt, noteSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAttachments = async (filesOverride) => {
    const pending = filesOverride?.length ? filesOverride : selectedFiles;
    const files = pending.length ? pending : Array.from(fileInputRef.current?.files || []);
    if (files.length === 0) return [];
    const fd = new FormData();
    files.forEach(file => fd.append("files", file, file.name || "attachment"));
    try {
      setUploading(true);
      const res = await apiFetch(`${FILES_URL}/upload`, { method: "POST", body: fd });
      if (!res.ok) { toast("Upload failed.", "error"); return []; }
      const uploaded = await res.json();
      const next = Array.isArray(uploaded) ? uploaded : [];
      setAttachments(prev => [...prev, ...next]);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return next;
    } catch { toast("Upload failed (network).", "error"); return []; }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (readOnly) return;
    const uploaded = await uploadAttachments();
    const payload = {
      ...buildPayload(),
      attachmentIds: [...new Set([
        ...attachments.map(f => f.id).filter(Boolean),
        ...uploaded.map(f => f.id).filter(Boolean),
        ...Array.from(selectedExistingIds),
      ])],
    };
    if (editNote) {
      await updateNote(editNote.id, payload);
      toast("Note saved.", "success");
    } else {
      const created = await createNote(payload);
      if (created?.id && payload.attachmentIds.length > 0) {
        await updateNote(created.id, payload);
      }
      nav("/notes");
    }
  };

  const fetchBlob = async (file) => {
    if (!file?.id) return null;
    const res = await apiFetch(`${FILES_URL}/${file.id}/download`);
    return res.ok ? res.blob() : null;
  };

  const openAttachment = async (file) => {
    const blob = await fetchBlob(file);
    if (!blob) { toast("Unable to open file.", "error"); return; }
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 5000);
  };

  const downloadAttachment = async (file) => {
    const blob = await fetchBlob(file);
    if (!blob) { toast("Download failed.", "error"); return; }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = file.fileName || "attachment";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const addTags = (raw) => {
    const next = raw.split(",").map(t => t.trim()).filter(Boolean);
    if (!next.length) return;
    setTags(prev => {
      const set = new Set(prev.map(t => t.toLowerCase()));
      const merged = [...prev];
      next.forEach(t => { if (!set.has(t.toLowerCase())) { merged.push(t.toUpperCase()); set.add(t.toLowerCase()); } });
      return merged;
    });
    setTagInput("");
  };

  const filteredExistingFiles = existingFiles.filter(f =>
    !fileSearch.trim() || (f.fileName || "").toLowerCase().includes(fileSearch.trim().toLowerCase())
  );

  const TYPE_LABEL = { INFO: "Info", REMINDER: "Reminder", WARNING: "Warning" };
  const TYPE_CLASS  = { INFO: "nf-type-info", REMINDER: "nf-type-reminder", WARNING: "nf-type-warning" };

  return (
    <div className={`nf-doc ${showSettings ? "nf-doc--settings-open" : ""}`}>

      {/* ── Top bar ── */}
      <div className="nf-topbar">
        <button type="button" className="nf-back-btn" onClick={() => nav("/notes")}>
          ← Notes
        </button>

        <div className="nf-topbar-center">
          <span className={`nf-type-badge ${TYPE_CLASS[noteType] || ""}`}>
            {TYPE_LABEL[noteType] || noteType}
          </span>
          <span className={`nf-autosave ${autoSaveStatus}`}>
            {autoSaveStatus === "saving" ? "Saving…" : autoSaveStatus === "saved" ? "Saved" : ""}
          </span>
        </div>

        <div className="nf-topbar-actions">
          {editNote?.id && (
            <button type="button" className="nf-icon-btn" title="Email note"
              onClick={async () => {
                const res = await emailNote(editNote.id);
                res.ok ? toast("Note emailed.", "success") : toast("Email failed.", "error");
              }}>
              ✉
            </button>
          )}
          {!readOnly && editNote?.id && (
            <button type="button" className="nf-icon-btn" title="Save as template"
              onClick={async () => { await saveAsTemplate({ title, content, noteType, priority }); toast("Saved as template.", "success"); }}>
              🗒
            </button>
          )}
          {!editNote && !readOnly && (
            <button type="button" className="nf-ghost-btn"
              onClick={async () => { const ut = await getUserTemplates(); setUserTemplates(ut); setShowTemplatePicker(true); }}>
              Templates
            </button>
          )}
          <button
            type="button"
            className={`nf-ghost-btn ${showSettings ? "nf-ghost-btn--active" : ""}`}
            onClick={() => setShowSettings(p => !p)}
          >
            Details
          </button>
          {!readOnly && (
            <button type="button" className="nf-save-btn" onClick={submit}>
              {editNote ? "Save" : "Create"}
            </button>
          )}
        </div>
      </div>

      {/* ── Document area ── */}
      <div className="nf-body">
        <div className="nf-doc-area">

          {/* Title */}
          <input
            className="nf-title-input"
            placeholder="Untitled…"
            value={title}
            onChange={e => setTitle(e.target.value)}
            disabled={readOnly}
          />

          {/* Inline tag row */}
          <div className="nf-tags-row">
            {tags.map(tag => (
              <span key={tag} className="nf-tag-chip">
                {tag}
                {!readOnly && (
                  <button type="button" className="nf-tag-x" onClick={() => setTags(prev => prev.filter(t => t !== tag))}>×</button>
                )}
              </span>
            ))}
            {!readOnly && (
              <input
                className="nf-tag-input"
                placeholder="+ tag"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTags(tagInput); }
                }}
                onBlur={() => { if (tagInput.trim()) addTags(tagInput); }}
              />
            )}
          </div>

          {/* Rich text editor */}
          <NoteEditor
            value={content}
            onChange={setContent}
            placeholder="Start writing…"
            readOnly={readOnly}
          />

          {/* Attachments — always visible below the editor */}
          {(attachments.length > 0 || !readOnly) && (
            <div className="nf-doc-attachments">
              <div className="nf-doc-attach-header">
                <span className="nf-doc-attach-label">
                  📎 Attachments {attachments.length > 0 ? `(${attachments.length})` : ""}
                </span>
                {!readOnly && (
                  <label className="nf-doc-attach-add" title="Upload file">
                    + Add file
                    <input type="file" multiple style={{ display: "none" }}
                      onChange={async e => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        await uploadAttachments(files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
              {attachments.length > 0 ? (
                <div className="nf-doc-attach-list">
                  {attachments.map(file => (
                    <div key={file.id} className="nf-doc-attach-item">
                      <span className="nf-doc-attach-icon">{fileTypeIcon(file.fileType)}</span>
                      <span className="nf-doc-attach-name" title={file.fileName}>{file.fileName || "file"}</span>
                      {file.fileSize && (
                        <span className="nf-doc-attach-size">{formatBytes(file.fileSize)}</span>
                      )}
                      <div className="nf-doc-attach-actions">
                        <button type="button" onClick={() => openAttachment(file)}>Open</button>
                        <button type="button" onClick={() => downloadAttachment(file)}>↓ Download</button>
                        {!readOnly && (
                          <button type="button" className="nf-doc-attach-remove"
                            onClick={() => setAttachments(p => p.filter(f => f.id !== file.id))}>×</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !readOnly && <p className="nf-doc-attach-empty">No attachments yet — add files above.</p>
              )}
            </div>
          )}

          {/* Links graph + history (collapsible, only in edit mode) */}
          {editNote?.id && (
            <div className="nf-panels">
              <NoteGraphPanel noteId={editNote.id} />
              {!readOnly && (
                <NoteHistoryPanel noteId={editNote.id} onRestored={() => editNote?.id && window.location.reload()} />
              )}
            </div>
          )}
        </div>

        {/* ── Settings sidebar ── */}
        {showSettings && (
          <aside className="nf-settings">
            <div className="nf-settings-header">
              <span>Details</span>
              <button type="button" className="nf-settings-close" onClick={() => setShowSettings(false)}>×</button>
            </div>

            {/* Type */}
            <div className="nf-field">
              <label className="nf-label">Type</label>
              <select className="nf-select" value={noteType} onChange={e => setNoteType(e.target.value)} disabled={readOnly}>
                <option value="INFO">Info</option>
                <option value="REMINDER">Reminder</option>
                <option value="WARNING">Warning</option>
              </select>
            </div>

            {/* Status */}
            <div className="nf-field">
              <label className="nf-label">Status</label>
              <select className="nf-select" value={status} onChange={e => setStatus(e.target.value)} disabled={readOnly}>
                <option value="ACTIVE">Active</option>
                <option value="ARCHIVED">Archived</option>
                <option value="DONE">Done</option>
                <option value="DELETED">Deleted</option>
              </select>
            </div>

            {/* Priority */}
            <div className="nf-field">
              <label className="nf-label">Priority</label>
              <div className="nf-priority-row">
                {[1,2,3,4,5].map(p => (
                  <button key={p} type="button"
                    className={`nf-priority-btn ${priority === p ? "active" : ""}`}
                    onClick={() => setPriority(p)} disabled={readOnly}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Reminder */}
            <div className="nf-field">
              <label className="nf-label">Reminder</label>
              <input className="nf-input" type="datetime-local" value={reminderAt}
                onChange={e => setReminderAt(e.target.value)} disabled={readOnly} />
            </div>

            {/* Flags */}
            <div className="nf-field">
              <label className="nf-label">Flags</label>
              <div className="nf-flag-row">
                <button type="button" className={`nf-flag-btn ${pinned ? "active" : ""}`}
                  onClick={() => setPinned(p => !p)} disabled={readOnly}>
                  📌 Pin
                </button>
                <button type="button" className={`nf-flag-btn ${favorite ? "active" : ""}`}
                  onClick={() => setFavorite(p => !p)} disabled={readOnly}>
                  ⭐ Fav
                </button>
              </div>
              <div className="nf-flag-row" style={{ marginTop: 6 }}>
                <button type="button" className={`nf-flag-btn ${secured ? "active" : ""}`}
                  onClick={() => setSecured(p => !p)} disabled={readOnly}>
                  🔒 Secure
                </button>
                <button type="button" className={`nf-flag-btn ${deleteProtected ? "active" : ""}`}
                  onClick={() => setDeleteProtected(p => !p)} disabled={readOnly}>
                  🛡 Protect
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="nf-field">
              <label className="nf-label">Summary</label>
              <textarea className="nf-textarea" rows={3} value={noteSummary}
                onChange={e => setNoteSummary(e.target.value)}
                placeholder="Key takeaway" disabled={readOnly} />
            </div>

            {/* Link existing files from vault */}
            {!readOnly && (
              <div className="nf-field">
                <button type="button" className="nf-section-toggle"
                  onClick={() => setShowAttachPanel(p => !p)}>
                  {showAttachPanel ? "▾" : "▸"} Link from File Vault
                </button>
                {showAttachPanel && (
                  <div className="nf-attach-panel">
                    <input className="nf-input" value={fileSearch} onChange={e => setFileSearch(e.target.value)} placeholder="Search files…" />
                    <div className="nf-file-picker">
                      {filteredExistingFiles.length === 0
                        ? <p className="nf-muted">No files found.</p>
                        : filteredExistingFiles.map(file => (
                            <label key={file.id} className="nf-file-option">
                              <input type="checkbox" checked={selectedExistingIds.has(file.id)}
                                onChange={() => setSelectedExistingIds(prev => {
                                  const next = new Set(prev);
                                  next.has(file.id) ? next.delete(file.id) : next.add(file.id);
                                  return next;
                                })} />
                              <span>{file.fileName || "Untitled"}</span>
                            </label>
                          ))
                      }
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* ── Template picker overlay ── */}
      {showTemplatePicker && (
        <div className="nf-overlay" onClick={() => setShowTemplatePicker(false)}>
          <div className="nf-template-picker" onClick={e => e.stopPropagation()}>
            <div className="nf-template-header">
              <strong>Choose a template</strong>
              <button type="button" onClick={() => setShowTemplatePicker(false)}>×</button>
            </div>
            <div className="nf-template-list">
              {[...BUILT_IN_TEMPLATES, ...userTemplates.map(n => ({ id: n.id, name: n.title, noteType: n.noteType, content: n.content }))].map(tpl => (
                <button key={tpl.id} type="button" className="nf-template-item"
                  onClick={() => {
                    setTitle(tpl.name.replace(" (template)", ""));
                    setContent(tpl.content);
                    setNoteType(tpl.noteType || "INFO");
                    setShowTemplatePicker(false);
                  }}>
                  {tpl.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
